import React, { useRef, useEffect } from 'react';
import { Info } from 'lucide-react';
import {
  CanvasElement,
  PageConfig,
  ColumnDefinition,
} from '../types';
import { generateQRMatrix } from '../utils/generators';
import { getCurrencyDisplay, isAsciiSafeSymbol, getCurrencyFallbackName } from '../utils/currency';

// ─── Helper ────────────────────────────────────────────────────────────────
const isSerialNoColumn = (col: ColumnDefinition) => {
  if (!col || !col.key || !col.header) return false;
  const k = col.key.toLowerCase();
  const h = col.header.toLowerCase();
  return (
    k === 'sno' || k === 'serial' || k === 'index' ||
    h.includes('s.no') || h.includes('sl.no') || h.includes('sr.no') ||
    h.includes('serial no') || h === 'no'
  );
};

// In multi_page mode, elements are absolutely positioned by y/height with
// no check for whether that box straddles the dashed page-break line — so
// a text block (like Notes/Terms) can render with its last line sliced
// by the boundary. This pushes any non-table element whose range crosses
// a page boundary down to the top of the next page instead.
// Tables are deliberately excluded — splitting a table's rows across a
// page break is a different, more involved fix, not this whole-element push.
function pushElementsClearOfPageBreaks(
  elements: CanvasElement[],
  pageHeight: number,
  pageCount: number
): CanvasElement[] {
  if (pageCount <= 1 || !pageHeight) return elements;

  console.log('CW elements before push:', elements.filter(e => e.type==='qr'||e.type==='text').map(e => ({id:e.id, type:e.type, x:e.x, y:e.y, w:e.width, h:e.height, text:e.textContent?.slice(0,20)})));

  // A QR (or stamp/signature) with a caption text sitting directly beneath
  // it is logically one unit — e.g. "Scan to Pay Net30" under its QR code.
  // Checking each element independently let the pair get torn apart: the
  // QR stays on page 1 while its label, starting a few px later, lands
  // just past the boundary and gets glued to the top of page 2 alone.
  // Build groups first so the pair moves together as a single box.
  const nonTable = elements.filter(el => el.type !== 'table');
  const used = new Set<string>();
  const groups: { ids: string[]; top: number; bottom: number }[] = [];

  const sortedByY = [...nonTable].sort((a, b) => (a.y || 0) - (b.y || 0));
  sortedByY.forEach(el => {
    if (used.has(el.id)) return;
    const top = el.y || 0;
    let bottom = top + (el.height || 0);
    const ids = [el.id];
    used.add(el.id);

    if (el.type === 'qr' || el.type === 'stamp' || el.type === 'signature') {
      // Find a text element starting close beneath this one's bottom edge,
      // roughly aligned horizontally — that's its caption.
      const caption = sortedByY.find(other =>
        !used.has(other.id) &&
        other.type === 'text' &&
        (other.y || 0) >= bottom - 2 &&
        (other.y || 0) <= bottom + 20 &&
        Math.abs((other.x || 0) - (el.x || 0)) < (el.width || 0) + 40
      );
      if (caption) {
        used.add(caption.id);
        ids.push(caption.id);
        bottom = Math.max(bottom, (caption.y || 0) + (caption.height || 0));
      }
    }
    groups.push({ ids, top, bottom });
  });

  groups.sort((a, b) => a.top - b.top);

  let cumulativeShift = 0;
  const yOverrides = new Map<string, number>();
  groups.forEach(group => {
    const top = group.top + cumulativeShift;
    const height = group.bottom - group.top;
    const bottom = top + height;
    const pageOfTop = Math.floor(top / pageHeight);
    const pageOfBottom = Math.floor((bottom - 0.01) / pageHeight);
    let shift = cumulativeShift;
    if (height > 0 && pageOfTop !== pageOfBottom) {
      const nextPageTop = (pageOfTop + 1) * pageHeight;
      const extraShift = nextPageTop - top;
      cumulativeShift += extraShift;
      shift += extraShift;
    }
    group.ids.forEach(id => {
      const el = elements.find(e => e.id === id);
      if (el) yOverrides.set(id, (el.y || 0) + shift);
    });
  });

  return elements.map(el => {
    if (el.type === 'table') return el;
    const overriddenY = yOverrides.get(el.id);
    return overriddenY !== undefined ? { ...el, y: overriddenY } : el;
  });
}

// Closes the gap that appears when a table shrinks (rows removed) but
// everything below it (QR, notes, signature, etc.) is still parked at
// its old fixed y. Shifts elements below the table up by the difference
// between the table's stored height and its real rendered height.
function closeGapsBelowTables(
  elements: CanvasElement[],
  measuredTableHeights: Record<string, number>
): CanvasElement[] {
  const tables = elements.filter(el => el.type === 'table');
  if (tables.length === 0) return elements;

  let result = [...elements];

  tables.forEach(table => {
    const storedBottom = (table.y || 0) + (table.height || 0);
    const realHeight = measuredTableHeights[table.id];
    if (realHeight == null) return;

    const realBottom = (table.y || 0) + realHeight;
    const delta = storedBottom - realBottom;
    if (Math.abs(delta) < 1) return;

    result = result.map(el => {
      if (el.id === table.id) return el;
      if ((el.y || 0) >= storedBottom - 2) {
        return { ...el, y: (el.y || 0) - delta };
      }
      return el;
    });
  });

  return result;
}

// ─── Props ──────────────────────────────────────────────────────────────────
export interface CanvasWorkspaceProps {
  // Layout
  pageConfig: PageConfig;
  computedHeight: number;
  pageCount: number;
  pageBehavior: 'fixed' | 'auto_expand' | 'multi_page';
  setPageBehavior: (b: 'fixed' | 'auto_expand' | 'multi_page') => void;
  zoom: number;
  setZoom: (z: number) => void;
  showGridLines: boolean;

  // Elements
  elements: CanvasElement[];
  selectedElementId: string | null;
  inlineEditingId: string | null;
  inlineEditText: string;
  setInlineEditText: (t: string) => void;
  lockedElementIds: string[];
  dragState: any;
  qrDataUrls: Record<string, string>;

  // Drawing
  isDrawingMode: boolean;
  isDrawingActive: boolean;
  currentPathPoints: { x: number; y: number }[];
  currentPenColor: string;
  currentPenWidth: number;

  // Bilingual
  bilingualMode: boolean;
  bilingualDirection: 'en-ar' | 'ar-en';
  translateTargetLang: string;

  // Currency
  activeCurrency: string;
  currencyDisplayMode: 'symbol' | 'code';

  // Computed
  computeTableTotals: (el: CanvasElement) => {
  subtotal: number; tax: number; secondTax?: number; discount: number; shipping: number; total: number; productTax?: number;
  taxBreakdown?: { name: string; rate: number; amount: number }[];
};
  formatSerialNumber: (settings: any) => string;

  // Handlers
  canvasRef: React.RefObject<HTMLDivElement>;
  handleMouseDown: (
    e: React.MouseEvent,
    el: CanvasElement,
    type: 'drag' | 'resize',
    direction?: 'nw' | 'ne' | 'sw' | 'se'
  ) => void;
  handleDeleteElement: (id: string) => void;
  handleInlineEditStart: (el: CanvasElement) => void;
  handleInlineEditSave: () => void;
  handleElementDoubleClick: (el: CanvasElement) => void;
  commitCurrentState: () => void;
  updateElementProps: (id: string, props: Partial<CanvasElement>) => void;
  handleCanvasMouseDown: (e: React.MouseEvent) => void;
  handleCanvasMouseMove: (e: React.MouseEvent) => void;
  handleCanvasMouseUp: () => void;
  onBackgroundClick: () => void;
  printMode?: boolean; // larger, print-appropriate table typography for PDF export
  onResetView?: () => void;
  mobilePreview?: boolean;
  readOnlyPreview?: boolean; // suppresses the amber "locked" ring — used for fully non-interactive previews (e.g. POS live preview) where lockedElementIds is set purely to disable drag/resize, not to visually flag a lock state
}

// ─── Component ──────────────────────────────────────────────────────────────
export default function CanvasWorkspace({
  pageConfig,
  computedHeight,
  pageCount,
  pageBehavior,
  setPageBehavior,
  zoom,
  setZoom,
  showGridLines,
  elements,
  selectedElementId,
  inlineEditingId,
  inlineEditText,
  setInlineEditText,
  lockedElementIds,
  dragState,
  qrDataUrls,
  isDrawingMode,
  isDrawingActive,
  currentPathPoints,
  currentPenColor,
  currentPenWidth,
  bilingualMode,
  bilingualDirection,
  translateTargetLang,
  activeCurrency,
  currencyDisplayMode,
  computeTableTotals,
  formatSerialNumber,
  canvasRef,
  handleMouseDown,
  handleDeleteElement,
  handleInlineEditStart,
  handleInlineEditSave,
  handleElementDoubleClick,
  commitCurrentState,
  updateElementProps,
  handleCanvasMouseDown,
  handleCanvasMouseMove,
  handleCanvasMouseUp,
  onBackgroundClick,
  printMode = false,
  onResetView,
  mobilePreview = false,
  readOnlyPreview = false,
}: CanvasWorkspaceProps) {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [measuredTableHeights, setMeasuredTableHeights] = React.useState<Record<string, number>>({});
  const tableRefs = useRef<Record<string, HTMLDivElement | null>>({});

  useEffect(() => {
    const observers: ResizeObserver[] = [];
    Object.entries(tableRefs.current).forEach(([id, node]) => {
      if (!node) return;
      const ro = new ResizeObserver(entries => {
        const h = entries[0].contentRect.height;
        setMeasuredTableHeights(prev => (prev[id] === h ? prev : { ...prev, [id]: h }));
      });
      ro.observe(node);
      observers.push(ro);
    });
    return () => observers.forEach(ro => ro.disconnect());
  }, [elements]);

// Mobile WebView double-tap detection.
// React/WebView onDoubleClick is unreliable for touch input.
const lastTapRef = useRef<{
  elementId: string;
  time: number;
} | null>(null);

  // Converts a touch event into a mouse-event-like object so existing
  // mouse handlers (handleMouseDown, etc.) can be reused for touch.
  const touchToMouseEvent = (e: React.TouchEvent): any => {
    const touch = e.touches[0] || e.changedTouches[0];
    return {
      ...e,
      clientX: touch.clientX,
      clientY: touch.clientY,
      stopPropagation: () => e.stopPropagation(),
      preventDefault: () => e.preventDefault(),
    };
  };


  useEffect(() => {
    const el = scrollContainerRef.current;
    if (!el) return;
    const handler = (e: WheelEvent) => {
      if (selectedElementId) e.preventDefault();
    };
    el.addEventListener('wheel', handler, { passive: false });
    return () => el.removeEventListener('wheel', handler);
  }, [selectedElementId]);

  const pinchStateRef = useRef<{ initialDistance: number; initialZoom: number } | null>(null);

useEffect(() => {
  const el = scrollContainerRef.current;
  if (!el) return;

  const getDistance = (touches: TouchList) => {
    const dx = touches[0].clientX - touches[1].clientX;
    const dy = touches[0].clientY - touches[1].clientY;
    return Math.hypot(dx, dy);
  };

  const handleTouchStart = (e: TouchEvent) => {
    if (e.touches.length === 2) {
      pinchStateRef.current = {
        initialDistance: getDistance(e.touches),
        initialZoom: zoom,
      };
    }
  };

  const handleTouchMove = (e: TouchEvent) => {
    if (e.touches.length === 2 && pinchStateRef.current) {
      e.preventDefault();
      const newDistance = getDistance(e.touches);
      const scaleFactor = newDistance / pinchStateRef.current.initialDistance;
      const newZoom = Math.min(1.4, Math.max(0.2, pinchStateRef.current.initialZoom * scaleFactor));
      setZoom(parseFloat(newZoom.toFixed(2)));
    }
  };

  const handleTouchEnd = () => {
    pinchStateRef.current = null;
  };

  el.addEventListener('touchstart', handleTouchStart, { passive: false });
  el.addEventListener('touchmove', handleTouchMove, { passive: false });
  el.addEventListener('touchend', handleTouchEnd);

  return () => {
    el.removeEventListener('touchstart', handleTouchStart);
    el.removeEventListener('touchmove', handleTouchMove);
    el.removeEventListener('touchend', handleTouchEnd);
  };
}, [zoom]);
  const currencySymbol = getCurrencyDisplay(activeCurrency, currencyDisplayMode);
  const displaySymbol = currencySymbol;
const symbolStyle = !isAsciiSafeSymbol(currencySymbol)
  ? { fontFamily: '"Noto Sans Arabic", "Arial Unicode MS", Arial, sans-serif' }
  : {};
  const arabizeNums = (s: string) =>
    translateTargetLang === 'ar'
      ? s.replace(/\d/g, d => '٠١٢٣٤٥٦٧٨٩'[+d])
      : s;
const totalLabels: Record<string, { subtotal: string; total: string; tax: string; discount: string; shipping: string }> = {
  ar: { subtotal: 'المجموع الفرعي', total: 'الإجمالي الكلي', tax: 'ضريبة', discount: 'خصم', shipping: 'الشحن' },
  ko: { subtotal: '소계', total: '합계', tax: '세금', discount: '할인', shipping: '배송' },
  hi: { subtotal: 'उप-कुल', total: 'कुल', tax: 'कर', discount: 'छूट', shipping: 'शिपिंग' },
  fr: { subtotal: 'Sous-total', total: 'Total', tax: 'Taxe', discount: 'Remise', shipping: 'Livraison' },
  de: { subtotal: 'Zwischensumme', total: 'Gesamt', tax: 'Steuer', discount: 'Rabatt', shipping: 'Versand' },
  es: { subtotal: 'Subtotal', total: 'Total', tax: 'Impuesto', discount: 'Descuento', shipping: 'Envío' },
  ja: { subtotal: '小計', total: '合計', tax: '税', discount: '割引', shipping: '配送' },
  zh: { subtotal: '小计', total: '总计', tax: '税', discount: '折扣', shipping: '运费' },
  ur: { subtotal: 'ذیلی کل', total: 'کل', tax: 'ٹیکس', discount: 'رعایت', shipping: 'ترسیل' },
  fa: { subtotal: 'جمع جزء', total: 'جمع کل', tax: 'مالیات', discount: 'تخفیف', shipping: 'حمل' },
  tr: { subtotal: 'Ara Toplam', total: 'Toplam', tax: 'Vergi', discount: 'İndirim', shipping: 'Kargo' },
  ru: { subtotal: 'Промежог', total: 'Итого', tax: 'Налог', discount: 'Скидка', shipping: 'Доставка' },
  pt: { subtotal: 'Subtotal', total: 'Total', tax: 'Imposto', discount: 'Desconto', shipping: 'Frete' },
  bn: { subtotal: 'উপমোট', total: 'মোট', tax: 'কর', discount: 'ছাড়', shipping: 'শিপিং' },
  ta: { subtotal: 'உப மொத்தம்', total: 'மொத்தம்', tax: 'வரி', discount: 'தள்ளுபடி', shipping: 'கப்பல்' },
};
const tl = totalLabels[translateTargetLang] || { subtotal: 'Sub', total: 'TOTAL', tax: 'Tax', discount: 'Discount', shipping: 'Shipping' };

  // The page-break push shifts elements downward, which can make real
  // content taller than the pageCount/computedHeight the parent computed
  // from pre-push positions — causing content to overflow past the paper's
  // bottom edge with no second page-break marker to show for it. Recompute
  // both from the pushed layout so the paper container and markers actually
  // match what's being rendered.
  const effectivePageData = React.useMemo(() => {
  // When mobilePreview is active, MobileInvoicePreview has already calculated exact uncolliding coordinates
  const gapClosed = mobilePreview ? elements : closeGapsBelowTables(elements, measuredTableHeights);

  const isA4OrLetter = pageConfig.width >= 700 || pageConfig.name?.toLowerCase().includes('a4') || pageConfig.name?.toLowerCase().includes('letter');

  // Fixed single page (Strict Standard A4 / Letter)
  if (pageBehavior === 'fixed' || (isA4OrLetter && pageBehavior !== 'auto_expand' && pageBehavior !== 'multi_page')) {
    return {
      renderElements: gapClosed,
      effectivePageCount: 1,
      effectiveHeight: pageConfig.height, // Strictly locked to standard A4 (1123px)
    };
  }

  // Thermal receipts: let content determine the length
  if (pageBehavior === 'auto_expand') {
    const maxBottom = gapClosed.reduce(
      (max, el) => Math.max(max, (el.y || 0) + (el.height || 0)),
      0
    );

    return {
      renderElements: gapClosed,
      effectivePageCount: 1,
      effectiveHeight: Math.max(computedHeight, maxBottom + 20),
    };
  }

  // A4 multi-page keeps the existing page-break behavior.
  const pushed = pushElementsClearOfPageBreaks(
    gapClosed,
    pageConfig.height,
    pageCount
  );

  const maxBottom = pushed.reduce(
    (max, el) => Math.max(max, (el.y || 0) + (el.height || 0)),
    0
  );

  const effectiveCount = Math.max(
    pageCount,
    Math.ceil((maxBottom + 20) / pageConfig.height)
  );

  return {
    renderElements: pushed,
    effectivePageCount: effectiveCount,
    effectiveHeight: pageConfig.height * effectiveCount,
  };
}, [
  elements,
  measuredTableHeights,
  pageBehavior,
  pageConfig.height,
  pageCount,
  computedHeight,
]);
  const { renderElements, effectivePageCount, effectiveHeight } = effectivePageData;

  return (
    <main className="flex-1 bg-slate-50 flex flex-col overflow-hidden relative">
      <style>{`
  @media (max-width: 768px) {
    .resize-handle-touch {
      width: 28px !important;
      height: 28px !important;
    }
    .rotation-handle-touch {
      width: 32px !important;
      height: 32px !important;
    }
    .delete-btn-touch {
      padding: 8px 14px !important;
      font-size: 12px !important;
      top: -38px !important;
    }
  }
`}</style>

      {/* Top rail */}
      <div 
        className="bg-white border-b border-slate-200 px-2 md:px-6 py-2.5 flex items-center justify-between shrink-0 text-xs shadow-xs z-10 gap-2"
        style={{ fontFamily: 'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif' }}
      >
  <div className="flex items-center space-x-2 text-slate-500 min-w-0">
    <span className="hidden md:inline font-semibold text-slate-700">Designer Viewboard:</span>
    <span className="px-2 py-0.5 bg-blue-50 rounded font-bold text-blue-600 text-[10px] truncate">{pageConfig.name}</span>
    <span className="hidden md:inline">•</span>
    <span className="hidden md:inline font-medium text-slate-600">Dimensions: {pageConfig.width}x{pageConfig.height}px</span>
  </div>

        {/* Page mode toggle */}
        <div className="flex items-center gap-0.5 bg-slate-100 rounded-md p-0.5 shrink-0">
          {([
            { key: 'fixed', label: 'Single' },
            { key: 'auto_expand', label: 'Auto' },
            { key: 'multi_page', label: 'A4 Split' },
          ] as const).map(opt => (
            <button
              key={opt.key}
              onClick={() => setPageBehavior(opt.key)}
              className={`px-2 py-1 text-[10px] font-bold rounded transition-colors ${
                pageBehavior === opt.key
                  ? 'bg-white text-blue-600 shadow-sm'
                  : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              {opt.label}
            </button>
          ))}
          {pageBehavior === 'multi_page' && pageCount > 1 && (
            <span className="ml-1 px-1.5 text-[10px] font-mono text-slate-400">
              {pageCount}p
            </span>
          )}
        </div>

        <div className="flex items-center space-x-1.5 md:space-x-3 text-slate-500 select-none shrink-0">
  <span className="hidden md:flex items-center gap-1 font-medium">
    <svg className="w-3.5 h-3.5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 6h18M3 12h18M3 18h18" />
    </svg>
    Zoom:
  </span>
  <input
    type="range" min="0.2" max="1.4" step="0.05"
    value={zoom}
    onChange={(e) => setZoom(parseFloat(e.target.value))}
    className="w-14 md:w-24 accent-blue-600 h-1.5 bg-slate-100 rounded mt-0.5"
  />
  <span className="font-mono text-slate-700 w-8 font-bold text-center text-[10px] md:text-xs">
  {Math.round(zoom * 100)}%
</span>

<button
  type="button"
  onClick={() => {
  if (onResetView) {
    onResetView();
  } else {
    setZoom(0.5);
  }
}}
  className="px-2 py-1 text-[10px] md:text-xs font-bold bg-slate-100 hover:bg-slate-200 text-slate-700 rounded"
>
  Reset
</button>
</div>
      </div>

      {/* Virtual desktop */}
      <div
  ref={scrollContainerRef}
  onClick={onBackgroundClick}
  className="flex-1 overflow-auto pt-4 pb-12 flex items-start bg-[#f8fafc] bg-[radial-gradient(#cbd5e1_1px,transparent_1px)] [background-size:20px_20px] scrollbar-thin"
  style={{
    paddingBottom: '100px',
    paddingLeft: '14px',
    paddingRight: '62px',
    scrollBehavior: 'auto',
    // 'center' alone makes flex overflow scrollable only on one side —
    // a well-known cross-browser bug. 'safe center' keeps it centered
    // when it fits, but falls back to reachable start-alignment once
    // it overflows, so both left and right stay scrollable.
    justifyContent: 'safe center',
  }}
>
        {/* Paper wrapper — sized to the ACTUAL on-screen (scaled) dimensions.
            transform:scale() doesn't shrink the box's footprint in flex
            layout, only how it's painted — so without this wrapper the
            flex container was centering a full-size (unscaled) invisible
            box, and once that overflowed a narrow screen, 'safe center'
            fell back to left-alignment, leaving the *visible* shrunken
            page stranded off to one side instead of centered. */}
        <div
          style={{
            width: pageConfig.width * zoom,
            height: effectiveHeight * zoom,
            position: 'relative',
            flexShrink: 0,
          }}
        >
        <div
          ref={canvasRef}
          onMouseDown={isDrawingMode ? handleCanvasMouseDown : undefined}
          onMouseMove={isDrawingMode ? handleCanvasMouseMove : undefined}
          onMouseUp={isDrawingMode ? handleCanvasMouseUp : undefined}
          onMouseLeave={isDrawingMode ? handleCanvasMouseUp : undefined}
          style={{
  width: pageConfig.width,
  height: effectiveHeight,
  transform: `scale(${zoom})`,
  transformOrigin: 'top left',
  transition: 'transform 0.1s ease-out',
  cursor: isDrawingMode ? 'crosshair' : 'default',
  position: 'absolute',
  top: 0,
  left: 0,
}}
          className="bg-white shadow-xl relative border border-slate-200 overflow-visible shrink-0"
        >
          {/* Grid overlay */}
          {showGridLines && (
            <div
              className="absolute inset-0 pointer-events-none select-none"
              style={{
                backgroundImage:
                  'linear-gradient(to right, rgba(99,102,241,0.05) 1px, transparent 1px), linear-gradient(to bottom, rgba(99,102,241,0.05) 1px, transparent 1px)',
                backgroundSize: '20px 20px',
              }}
            />
          )}

          {/* Page break markers — multi_page shows real page boundaries.
              auto_expand/fixed never drew anything here before, so
              content taller than one physical page (1123px for A4) looked
              fine in-app but still got sliced onto page 2 by Puppeteer at
              export time. This adds a warning line at the exact same
              pixel Puppeteer will cut, in every mode, so what you see
              here is what the PDF will actually do. */}
          {(() => {
  const isMultiPage = pageBehavior === 'multi_page';

  // Thermal / auto-expand receipts have no artificial A4 page markers.
  if (!isMultiPage) return null;

  const printPageCount = effectivePageCount;

  if (printPageCount <= 1) return null;
            return Array.from({ length: printPageCount - 1 }).map((_, idx) => (
              <div
                key={`pb-${idx}`}
                style={{ top: pageConfig.height * (idx + 1) }}
                className={`absolute left-0 w-full h-0 border-t-2 z-[99] pointer-events-none select-none flex items-center justify-end ${
                  isMultiPage ? 'border-dashed border-rose-450' : 'border-dotted border-amber-400'
                }`}
              >
                <span className={`text-[10px] font-bold tracking-wider uppercase px-2.5 py-1 rounded shadow-sm translate-y-2 mr-4 select-none ${
                  isMultiPage
                    ? 'bg-rose-50 border border-rose-100 text-rose-700'
                    : 'bg-amber-50 border border-amber-200 text-amber-700'
                }`}>
                  {isMultiPage
                    ? `Page Segment #${idx + 2} Top Border`
                    : `⚠ Page ${idx + 1} ends here · Page ${idx + 2} starts below`}
                </span>
              </div>
            ));
          })()}

          {/* Live drawing preview */}
          {isDrawingActive && currentPathPoints.length > 0 && (
            <svg className="absolute inset-0 pointer-events-none select-none z-[9999] w-full h-full">
              <path
                d={`M ${currentPathPoints.map(p => `${p.x} ${p.y}`).join(' L ')}`}
                stroke={currentPenColor}
                strokeWidth={currentPenWidth}
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          )}

          {/* ── Element render loop ── */}
          {renderElements.filter(el => el.type !== 'barcode').map((el) => {
            const isSelected = el.id === selectedElementId;
            const isInlineEditing = el.id === inlineEditingId;
            const computedTotals = el.type === 'table' ? computeTableTotals(el) : null;

            return (
              <div
  key={el.id}
  data-elid={el.id}
  onMouseDown={(e) => {
    if (lockedElementIds.includes(el.id)) return;
    handleMouseDown(e, el, 'drag');
  }}
  onTouchStart={(e) => {
  if (lockedElementIds.includes(el.id)) return;

  const now = Date.now();
  const lastTap = lastTapRef.current;

  const isDoubleTap =
    lastTap &&
    lastTap.elementId === el.id &&
    now - lastTap.time < 350;

  if (isDoubleTap) {
    e.stopPropagation();
    e.preventDefault();

    lastTapRef.current = null;

    // Mobile double tap -> open Properties.
    handleElementDoubleClick(el);
    return;
  }

  // Remember first tap.
  lastTapRef.current = {
    elementId: el.id,
    time: now,
  };

  // Normal first touch keeps existing select/drag behaviour.
  handleMouseDown(
    touchToMouseEvent(e),
    el,
    'drag',
  );
}}
                onClick={(e) => { 
  e.stopPropagation();
  if (!lockedElementIds.includes(el.id)) {
    // selection is handled by mouseDown, nothing needed here
  }
}}
onWheel={(e) => {
  if (!isSelected) return;
  if (el.type !== 'text' && el.type !== 'stamp' && el.type !== 'signature' && el.type !== 'serial') return;
  e.stopPropagation();
  e.preventDefault();
  const delta = e.deltaY > 0 ? -1 : 1;
  const newSize = Math.max(6, Math.min(120, (el.fontSize || 14) + delta));
  updateElementProps(el.id, { fontSize: newSize });
  clearTimeout((window as any).__scrollFontTimer__);
  (window as any).__scrollFontTimer__ = setTimeout(() => {
    commitCurrentState();
  }, 600);
}}
                onDoubleClick={(e) => {
  e.stopPropagation();
  handleElementDoubleClick(el);
}}
                style={{
                  position: 'absolute',
                  left: el.x,
                  top: el.y,
                  width: el.width,
                  height: (el.type === 'table' || el.type === 'qr') ? 'fit-content' : el.height,
                  minHeight: undefined,
                  zIndex: el.zIndex,
                  opacity: (el.opacity ?? 100) / 100,
                  cursor: dragState?.id === el.id ? 'grabbing' : 'grab',
                  overflow: isSelected || el.type === 'stamp' || el.type === 'qr' ? 'visible' : undefined,
                  transform: `rotate(${el.type === 'shape' ? (el.rotation||0) : 0}deg) scaleX(${el.flipX ? -1 : 1}) scaleY(${el.flipY ? -1 : 1})`,
                  transformOrigin: 'center center',
                  mixBlendMode: (el.blendMode || 'normal') as any,
                  filter: el.shadowEnabled
                    ? `drop-shadow(${el.shadowX||0}px ${el.shadowY||4}px ${el.shadowBlur||8}px ${el.shadowColor||'#000000'}${Math.round(((el.shadowOpacity??40)/100)*255).toString(16).padStart(2,'0')})`
                    : undefined,
                }}
                className={`group transition-shadow duration-150 relative ${
  isSelected
    ? 'ring-2 ring-blue-500 shadow-lg bg-blue-50/10'
    : 'hover:ring-1 hover:ring-slate-350 hover:bg-slate-50/10'
} ${(!readOnlyPreview && lockedElementIds.includes(el.id)) ? 'ring-1 ring-amber-400' : ''} ${
  isSelected || el.type === 'stamp' || el.type === 'table' || el.type === 'text' || el.type === 'qr' ? 'overflow-visible' : 'overflow-hidden'
}`}
              >
                {/* ── Selection handles ── */}
                {isSelected && (
                  <>
                    {/* Font size stepper — text/stamp/signature/serial only */}
                    {(el.type === 'text' || el.type === 'stamp' || el.type === 'signature' || el.type === 'serial') && (
                      <div
                        onClick={(e) => e.stopPropagation()}
                        onMouseDown={(e) => e.stopPropagation()}
                        onTouchStart={(e) => e.stopPropagation()}
                        className="absolute -top-7 left-0 flex items-center gap-0.5 bg-slate-900 rounded px-1 py-0.5 z-[110] shadow-md select-none"
                      >
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            const newSize = Math.max(6, (el.fontSize || 14) - 1);
                            updateElementProps(el.id, { fontSize: newSize });
                            commitCurrentState();
                          }}
                          onTouchEnd={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            const newSize = Math.max(6, (el.fontSize || 14) - 1);
                            updateElementProps(el.id, { fontSize: newSize });
                            commitCurrentState();
                          }}
                          className="w-5 h-5 flex items-center justify-center text-white text-[13px] font-bold hover:bg-slate-700 rounded"
                        >
                          −
                        </button>
                        <span className="text-white text-[10px] font-mono w-6 text-center">
                          {el.fontSize || 14}
                        </span>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            const newSize = Math.min(120, (el.fontSize || 14) + 1);
                            updateElementProps(el.id, { fontSize: newSize });
                            commitCurrentState();
                          }}
                          onTouchEnd={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            const newSize = Math.min(120, (el.fontSize || 14) + 1);
                            updateElementProps(el.id, { fontSize: newSize });
                            commitCurrentState();
                          }}
                          className="w-5 h-5 flex items-center justify-center text-white text-[13px] font-bold hover:bg-slate-700 rounded"
                        >
                          +
                        </button>
                      </div>
                    )}

                    {/* Delete button */}
<button
  onClick={(e) => {
    e.stopPropagation();
    e.preventDefault();

    console.log('[Canvas Delete] click:', el.id);
    handleDeleteElement(el.id);
  }}
  onTouchEnd={(e) => {
    e.stopPropagation();
    e.preventDefault();

    console.log('[Canvas Delete] touch:', el.id);
    handleDeleteElement(el.id);
  }}
  className="delete-btn-touch absolute -top-7 right-0 bg-rose-600 hover:bg-rose-700 text-white rounded px-2 py-0.5 hover:scale-105 transition-all text-[9.5px] font-bold shadow-md z-[110] flex items-center gap-1.5 select-none cursor-pointer border border-rose-500 whitespace-nowrap"
>
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                      <span>Delete</span>
                    </button>

                 {/* Resize handles */}
{(['nw','ne','sw','se'] as const).map(dir => (
  <div
    key={dir}
    onMouseDown={(e) => { if (!lockedElementIds.includes(el.id)) handleMouseDown(e, el, 'resize', dir); }}
    onTouchStart={(e) => { if (!lockedElementIds.includes(el.id)) handleMouseDown(touchToMouseEvent(e), el, 'resize', dir); }}
    className={`absolute w-3.5 h-3.5 resize-handle-touch bg-blue-600 hover:bg-blue-500 border border-white rounded-full z-[100] ${
      dir === 'nw' ? '-top-1.5 -left-1.5 cursor-nwse-resize' :
      dir === 'ne' ? '-top-1.5 -right-1.5 cursor-nesw-resize' :
      dir === 'sw' ? '-bottom-1.5 -left-1.5 cursor-nesw-resize' :
                     '-bottom-1.5 -right-1.5 cursor-nwse-resize'
    }`}
  />
))}

                    {/* Rotation stem */}
                    <div style={{ position:'absolute', left:'50%', top:'-28px', width:'1.5px', height:'22px', background:'#3b82f6', transform:'translateX(-50%)', pointerEvents:'none' }} />

                    {/* Rotation handle */}
<div
  className="rotation-handle-touch"
  style={{ position:'absolute', left:'50%', top:'-44px', transform:'translateX(-50%)', width:'18px', height:'18px', background:'white', border:'1.5px solid #3b82f6', borderRadius:'50%', cursor:'grab', zIndex:110, display:'flex', alignItems:'center', justifyContent:'center' }}
  onMouseDown={(e) => {
    e.stopPropagation();
    const rect = canvasRef.current!.getBoundingClientRect();
    const cx = el.x + el.width / 2;
    const cy = el.y + el.height / 2;
    const onMove = (ev: MouseEvent) => {
      const mx = (ev.clientX - rect.left) / zoom;
      const my = (ev.clientY - rect.top) / zoom;
      let angle = Math.atan2(my - cy, mx - cx) * (180 / Math.PI) + 90;
      if (ev.shiftKey) angle = Math.round(angle / 15) * 15;
      updateElementProps(el.id, { rotation: Math.round(angle) });
    };
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      commitCurrentState();
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }}
  onTouchStart={(e) => {
    e.stopPropagation();
    const rect = canvasRef.current!.getBoundingClientRect();
    const cx = el.x + el.width / 2;
    const cy = el.y + el.height / 2;
    const onMove = (ev: TouchEvent) => {
      ev.preventDefault();
      const touch = ev.touches[0];
      if (!touch) return;
      const mx = (touch.clientX - rect.left) / zoom;
      const my = (touch.clientY - rect.top) / zoom;
      let angle = Math.atan2(my - cy, mx - cx) * (180 / Math.PI) + 90;
      updateElementProps(el.id, { rotation: Math.round(angle) });
    };
    const onUp = () => {
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('touchend', onUp);
      commitCurrentState();
    };
    window.addEventListener('touchmove', onMove, { passive: false });
    window.addEventListener('touchend', onUp);
  }}
>
                      <svg width="10" height="10" viewBox="0 0 14 14" fill="none" stroke="#3b82f6" strokeWidth="1.5" strokeLinecap="round">
                        <path d="M2.5 7A4.5 4.5 0 1 0 7 2.5" />
                        <polyline points="4.5,0.5 2.5,2.5 4.5,4.5" />
                      </svg>
                      {(el.rotation !== undefined && el.rotation !== 0) && (
                        <div style={{ position:'absolute', top:'-22px', left:'50%', transform:'translateX(-50%)', background:'#3b82f6', color:'white', fontSize:'9px', fontWeight:'bold', padding:'1px 5px', borderRadius:'8px', whiteSpace:'nowrap', pointerEvents:'none' }}>
                          {el.rotation}°
                        </div>
                      )}
                    </div>
                  </>
                )}

                {/* ── Inline textarea ── */}
                {isInlineEditing ? (
                  <textarea
                    autoFocus
                    value={inlineEditText}
                    onChange={(e) => setInlineEditText(e.target.value)}
                    onBlur={handleInlineEditSave}
                    onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleInlineEditSave(); } }}
                    style={{ width:'100%', height:'100%', fontSize:`${el.fontSize}px`, fontFamily: el.fontFamily || 'Inter' }}
                    className="bg-slate-100 border border-blue-500 text-slate-900 rounded p-1 outline-none resize-none font-medium text-xs"
                  />
                ) : (

                  /* ── Element type renders ── */
                  <div className={`w-full h-full text-slate-900 leading-tight flex-1 flex flex-col justify-stretch ${el.type === 'text' || el.type === 'stamp' || el.type === 'table' ? 'overflow-visible' : 'overflow-hidden'}`}>

                    {/* SHAPE */}
                    {el.type === 'shape' && (() => {
                      const bColor = el.borderColor || el.color || '#000000';
                      const bW = el.borderWidth || 0;
                      const shp = el.shapeType || 'rectangle';
                      const lineStyle = el.lineStyle || 'solid';
                      const dashSize = el.dashSize || 8;
                      const gapSize = el.gapSize || 4;
                      const strokeAlign = el.strokeAlign || 'center';
                      const svgStroke = bW > 0 ? bColor : 'none';
                      // stroke-align: inside = strokeWidth*2 but clipped, outside = expand, center = default
                      const svgStrokeW = strokeAlign === 'inside' ? bW * 4 : strokeAlign === 'outside' ? bW * 2 : bW * 2;
                      const clipForInside = strokeAlign === 'inside';

                      // Fill — solid, gradient, or none
                      const fillType = el.fillType || 'solid';
                      const gradId = `grad-${el.id}`;
                      let fillRef = 'transparent';
                      if (fillType === 'solid') fillRef = el.backgroundColor || 'transparent';
                      else if (fillType === 'linear' || fillType === 'radial') fillRef = `url(#${gradId})`;
                      else if (fillType === 'none') fillRef = 'transparent';

                      // Per-corner radius for rect (mapped to 0-100 viewBox scale)
                      const scale = 1 / Math.max(el.width || 1, el.height || 1) * 100;
                      const rxTL = (el.borderRadiusTL ?? el.borderRadius ?? 0) * scale;
                      const rxTR = (el.borderRadiusTR ?? el.borderRadius ?? 0) * scale;
                      const rxBL = (el.borderRadiusBL ?? el.borderRadius ?? 0) * scale;
                      const rxBR = (el.borderRadiusBR ?? el.borderRadius ?? 0) * scale;
                      const hasPerCorner = rxTL !== rxTR || rxTL !== rxBL || rxTL !== rxBR;

                      // Dash array
                      const dashArray = lineStyle === 'dashed' ? `${dashSize},${gapSize}`
                        : lineStyle === 'dotted' ? `${bW},${bW * 2}`
                        : undefined;

                      // Gradient defs helper
                      const GradDefs = () => (
                        <defs>
                          {fillType === 'linear' && (
                            <linearGradient id={gradId}
                              x1={`${Math.cos(((el.gradientAngle||0)-90)*Math.PI/180)*50+50}%`}
                              y1={`${Math.sin(((el.gradientAngle||0)-90)*Math.PI/180)*50+50}%`}
                              x2={`${Math.cos(((el.gradientAngle||180)-90)*Math.PI/180)*50+50}%`}
                              y2={`${Math.sin(((el.gradientAngle||180)-90)*Math.PI/180)*50+50}%`}>
                              <stop offset="0%" stopColor={el.gradientFrom||'#ffffff'}/>
                              <stop offset="100%" stopColor={el.gradientTo||'#000000'}/>
                            </linearGradient>
                          )}
                          {fillType === 'radial' && (
                            <radialGradient id={gradId} cx="50%" cy="50%" r="50%">
                              <stop offset="0%" stopColor={el.gradientFrom||'#ffffff'}/>
                              <stop offset="100%" stopColor={el.gradientTo||'#000000'}/>
                            </radialGradient>
                          )}
                          {clipForInside && <clipPath id={`clip-${el.id}`}><rect x="0" y="0" width="100" height="100"/></clipPath>}
                        </defs>
                      );

                      const commonStroke = {
                        stroke: svgStroke,
                        strokeWidth: svgStrokeW,
                        strokeDasharray: dashArray,
                        strokeLinecap: 'round' as const,
                        clipPath: clipForInside ? `url(#clip-${el.id})` : undefined,
                      };

                      // LINE
                      if (shp === 'line') {
                        const lh = Math.max(1, bW || 2);
                        return (
                          <svg className="w-full h-full overflow-visible" preserveAspectRatio="none">
                            <GradDefs />
                            <line x1="0" y1="50%" x2="100%" y2="50%"
                              stroke={bColor||'#000'} strokeWidth={lh}
                              strokeDasharray={dashArray} strokeLinecap="round"/>
                            {lineStyle === 'double' && <>
                              <line x1="0" y1={`calc(50% - ${lh+2}px)`} x2="100%" y2={`calc(50% - ${lh+2}px)`} stroke={bColor||'#000'} strokeWidth={lh}/>
                              <line x1="0" y1={`calc(50% + ${lh+2}px)`} x2="100%" y2={`calc(50% + ${lh+2}px)`} stroke={bColor||'#000'} strokeWidth={lh}/>
                            </>}
                          </svg>
                        );
                      }

                      // CIRCLE
                      if (shp === 'circle') return (
                        <svg className="w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
                          <GradDefs />
                          <ellipse cx="50" cy="50" rx={50-bW} ry={50-bW} fill={fillRef} {...commonStroke}/>
                        </svg>
                      );

                      // TRIANGLE
                      if (shp === 'triangle') return (
                        <svg viewBox="0 0 100 100" className="w-full h-full" preserveAspectRatio="none">
                          <GradDefs />
                          <polygon points="50,5 95,95 5,95" fill={fillRef} {...commonStroke}/>
                        </svg>
                      );

                      // STAR
                      if (shp === 'star') return (
                        <svg viewBox="0 0 100 100" className="w-full h-full" preserveAspectRatio="none">
                          <GradDefs />
                          <polygon points="50,2 64,36 100,36 71,57 81,91 50,70 19,91 29,57 0,36 36,36" fill={fillRef} {...commonStroke}/>
                        </svg>
                      );

                      // ARROW
                      if (shp === 'arrow') return (
                        <svg viewBox="0 0 100 100" className="w-full h-full" preserveAspectRatio="none">
                          <GradDefs />
                          <path d="M 5,40 L 70,40 L 70,15 L 95,50 L 70,85 L 70,60 L 5,60 Z" fill={fillRef} {...commonStroke}/>
                        </svg>
                      );

                      // DIAMOND
                      if (shp === 'diamond') return (
                        <svg viewBox="0 0 100 100" className="w-full h-full" preserveAspectRatio="none">
                          <GradDefs />
                          <polygon points="50,2 98,50 50,98 2,50" fill={fillRef} {...commonStroke}/>
                        </svg>
                      );

                      // PENTAGON
                      if (shp === 'pentagon') return (
                        <svg viewBox="0 0 100 100" className="w-full h-full" preserveAspectRatio="none">
                          <GradDefs />
                          <polygon points="50,2 98,35 79,95 21,95 2,35" fill={fillRef} {...commonStroke}/>
                        </svg>
                      );

                      // HEXAGON
                      if (shp === 'hexagon') return (
                        <svg viewBox="0 0 100 100" className="w-full h-full" preserveAspectRatio="none">
                          <GradDefs />
                          <polygon points="25,2 75,2 98,50 75,98 25,98 2,50" fill={fillRef} {...commonStroke}/>
                        </svg>
                      );

                      // CROSS
                      if (shp === 'cross') return (
                        <svg viewBox="0 0 100 100" className="w-full h-full" preserveAspectRatio="none">
                          <GradDefs />
                          <path d="M35,0 L65,0 L65,35 L100,35 L100,65 L65,65 L65,100 L35,100 L35,65 L0,65 L0,35 L35,35 Z" fill={fillRef} {...commonStroke}/>
                        </svg>
                      );

                      // RECTANGLE (default) — per-corner radius support
                      const inset = bW;
                      const w = 100 - inset * 2;
                      const h = 100 - inset * 2;
                      const rectPath = hasPerCorner
                        ? `M ${inset+rxTL},${inset} H ${inset+w-rxTR} Q ${inset+w},${inset} ${inset+w},${inset+rxTR} V ${inset+h-rxBR} Q ${inset+w},${inset+h} ${inset+w-rxBR},${inset+h} H ${inset+rxBL} Q ${inset},${inset+h} ${inset},${inset+h-rxBL} V ${inset+rxTL} Q ${inset},${inset} ${inset+rxTL},${inset} Z`
                        : null;

                      return (
                        <svg className="w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
                          <GradDefs />
                          {rectPath
                            ? <path d={rectPath} fill={fillRef} {...commonStroke}/>
                            : <rect x={inset} y={inset} width={w} height={h} fill={fillRef}
                                rx={(el.borderRadius||0)*scale} ry={(el.borderRadius||0)*scale}
                                {...commonStroke}/>
                          }
                        </svg>
                      );
                    })()}


                    {/* DRAWING */}
                    {el.type === 'drawing' && el.drawingPoints && (
                      <svg viewBox={`0 0 ${el.width} ${el.height}`} className="w-full h-full pointer-events-none overflow-visible" preserveAspectRatio="none">
                        <path d={`M ${el.drawingPoints.map(p=>`${p.x} ${p.y}`).join(' L ')}`} stroke={el.color||'#4f46e5'} strokeWidth={el.borderWidth||3} fill="none" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    )}

                    {/* IMAGE */}
                    {el.type === 'image' && (
                      <div className="w-full h-full flex items-center justify-center bg-slate-50">
                        {el.textContent
                          ? <img src={el.textContent} alt="" className="w-full h-full object-contain pointer-events-none" />
                          : isSelected
  ? <div className="w-full h-full border-2 border-dashed border-slate-300 rounded flex items-center justify-center">
      <span className="text-[9px] text-slate-400 select-none">Logo</span>
    </div>
  : null}
                      </div>
                    )}

                    {/* TEXT */}
                    {el.type === 'text' && (
                      <div
  className="w-full flex flex-row items-start"
  style={{
    minWidth: 0,
    gap: `${el.bilingualGap ?? 6}px`,
    overflow: mobilePreview ? 'visible' : 'hidden',
    minHeight: mobilePreview ? '100%' : undefined,
    height: mobilePreview ? 'auto' : '100%',
  }}
>
                        {bilingualDirection === 'ar-en' && bilingualMode && el.arabicContent && (
                          <div style={{ fontSize:`${Math.round((el.fontSize||14)*0.9)}px`, fontFamily:'"Noto Sans Arabic","Arial",sans-serif', color:el.color||'#000', textAlign:'right', lineHeight:el.lineHeight||1.3, whiteSpace:'pre-wrap', direction:'rtl', unicodeBidi:'plaintext', flex:'1 1 0', minWidth:0, wordBreak:'break-word', padding:'2px 4px', overflow:'hidden', height:'100%' }}>
                            {(el.arabicContent || '').replace(/\b(\d+)\b(?![A-Za-z])/g, n => translateTargetLang === 'ar' ? n.replace(/\d/g, d => '٠١٢٣٤٥٦٧٨٩'[+d]) : n)}
                          </div>
                        )}
                        <div style={{ fontSize:`${el.fontSize||14}px`, fontFamily:el.fontFamily||'Inter', fontWeight:el.fontWeight||'normal', fontStyle:el.fontStyle||'normal', color:el.color||'#000', textAlign:/[\u0600-\u06FF]/.test(el.textContent||'') ? 'right' : (el.textAlign||'left'), direction:/[\u0600-\u06FF]/.test(el.textContent||'') ? 'rtl' : 'ltr', lineHeight:el.lineHeight||1.3, whiteSpace:'pre-wrap', transform:`rotate(${el.rotation||0}deg)`, transformOrigin:'center', flex:'1 1 0', minWidth:0, wordBreak:'break-word', padding:'2px 4px', overflow:'visible' }}
                          className="select-text font-sans break-words outline-none">
                          {el.textContent || '(Double click to type)'}
                        </div>
                        {bilingualDirection === 'en-ar' && bilingualMode && el.arabicContent && (
                          <div style={{ fontSize:`${Math.round((el.fontSize||14)*0.9)}px`, fontFamily:'"Noto Sans Arabic","Arial",sans-serif', color:el.color||'#000', textAlign:'right', lineHeight:el.lineHeight||1.3, whiteSpace:'pre-wrap', direction:'rtl', unicodeBidi:'plaintext', flex:'1 1 0', minWidth:0, wordBreak:'break-word', padding:'2px 4px', overflow:'hidden', height:'100%' }}>
                            {(el.arabicContent || '').replace(/\b(\d+)\b(?![A-Za-z])/g, n => translateTargetLang === 'ar' ? n.replace(/\d/g, d => '٠١٢٣٤٥٦٧٨٩'[+d]) : n)}
                          </div>
                        )}
                      </div>
                    )}

                    {/* STAMP */}
                    {el.type === 'stamp' && (
                      <div
                        style={{ transform:`rotate(${el.rotation||0}deg)`, color:el.color||'#dc2626', borderColor:el.color||'#dc2626', fontSize:`${el.fontSize||18}px` }}
                        className={`w-full h-full text-center font-black tracking-widest uppercase flex flex-col justify-center items-center bg-transparent relative leading-none ${el.stampShape==='circle'||el.stampShape==='oval'?'':'border-4 border-double rounded'}`}
                      >
                        {(() => {
                          const c = el.color||'#dc2626';
                          if (el.stampShape==='circle') return <div className="absolute inset-0 border-4 border-double rounded-full" style={{borderColor:c}}><div className="absolute inset-1 border border-dashed rounded-full" style={{borderColor:`${c}55`}}/></div>;
                          if (el.stampShape==='oval') return <div className="absolute inset-0 border-4 border-double rounded-[50%]" style={{borderColor:c}}><div className="absolute inset-1 border border-dashed rounded-[50%]" style={{borderColor:`${c}55`}}/></div>;
                          if (el.stampShape==='pill') return <div className="absolute inset-0 border-4 border-double" style={{borderColor:c,borderRadius:'9999px'}}><div className="absolute inset-1 border border-dashed" style={{borderColor:`${c}55`,borderRadius:'9999px'}}/></div>;
                          if (el.stampShape==='badge') return <div className="absolute inset-0 border-4 border-double rounded-xl" style={{borderColor:c}}><div className="absolute inset-1 border border-dashed rounded-lg" style={{borderColor:`${c}55`}}/></div>;
                          return <div className="border w-full h-full absolute inset-0.5 pointer-events-none" style={{borderColor:`${c}66`}}/>;
                        })()}
                        {el.stampDraft && <div className="absolute inset-0 opacity-10 mix-blend-color-dodge pointer-events-none" style={{backgroundImage:'repeating-linear-gradient(45deg,transparent,transparent 1px,#fff 1px,#fff 2px)'}}/>}
                        <div className="relative z-10 select-none flex flex-col items-center justify-center px-1">
                          <span>{el.stampText||'APPROVED'}</span>
                          {bilingualMode && el.arabicContent && (
                            <span style={{ fontSize:'0.65em', fontFamily:'"Noto Sans Arabic",Arial,sans-serif', direction:'rtl', opacity:0.9, marginTop:'2px', color:el.color||'#dc2626' }}>{(el.arabicContent || '').replace(/\b(\d+)\b(?![A-Za-z])/g, n => translateTargetLang === 'ar' ? n.replace(/\d/g, d => '٠١٢٣٤٥٦٧٨٩'[+d]) : n)}</span>
                          )}
                        </div>
                      </div>
                    )}

                 {/* SIGNATURE */}
{el.type === 'signature' && (
  <div style={{ color: el.color || '#0f172a' }} className="w-full h-full flex flex-col justify-end items-center p-1 pb-1.5 select-none">
    <span style={{ fontFamily: el.fontFamily || 'Alex Brush', fontSize: el.fontSize ? `${el.fontSize}px` : '24px' }} className="tracking-wide text-center w-full truncate">
      {el.signatureName || 'Authorized'}
    </span>
    <div className="w-full h-px bg-slate-400/80 my-1" />
    <div className="flex w-full justify-center items-center gap-1" style={{overflow:'hidden'}}>
      <span style={{fontSize:'7px',color:'#64748b',fontWeight:'bold',textTransform:'uppercase',whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis',flex:'1',textAlign:'center'}}>
        {el.textContent || 'Authorized Signature'}
      </span>
      {bilingualMode && (el as any).arabicContent && (
        <span style={{fontSize:'7px',fontFamily:'"Noto Sans Arabic",sans-serif',direction:'rtl',color:'#64748b',whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis',flex:'1',textAlign:'center'}}>
          {arabizeNums((el as any).arabicContent || '')}
        </span>
      )}
    </div>
  </div>
)}

                    {/* QR */}
                    {el.type === 'qr' && (() => {
                      const showLabel = el.showLabel !== false && Boolean(el.label);
                      const isTop = el.labelPosition === 'top';
                      const labelText = el.label || '';
                      // The QR graphic is ALWAYS a locked 1:1 square matching the element width
                      const qrSize = Math.max(40, el.width || 80);

                      return (
                        <div className={`w-full bg-transparent flex ${isTop ? 'flex-col-reverse' : 'flex-col'} items-center justify-center p-1 select-none`}>
                          {/* QR Code Graphic (Strict 1:1 Vector Square — NEVER Squishes) */}
                          <div
                            style={{ width: `${qrSize}px`, height: `${qrSize}px` }}
                            className="shrink-0 flex items-center justify-center"
                          >
                            {qrDataUrls[el.id] ? (
                              <img
                                src={qrDataUrls[el.id]}
                                style={{ width: `${qrSize}px`, height: `${qrSize}px` }}
                                className="object-contain pointer-events-none block"
                                alt="QR"
                              />
                            ) : (() => {
                              const matrix = generateQRMatrix(el.qrValue || 'https://');
                              const size = matrix.length;
                              return (
                                <svg
                                  viewBox={`0 0 ${size} ${size}`}
                                  style={{ width: `${qrSize}px`, height: `${qrSize}px`, display: 'block' }}
                                  shapeRendering="crispEdges"
                                >
                                  <rect width={size} height={size} fill="#ffffff" />
                                  {matrix.map((row, r) =>
                                    row.map((dark, c) =>
                                      dark ? <rect key={`${r}-${c}`} x={c} y={r} width="1" height="1" fill="#0f172a" /> : null
                                    )
                                  )}
                                </svg>
                              );
                            })()}
                          </div>

                          {/* Bundled Caption Label (Comfortable space underneath, no clipping) */}
                          {showLabel && (
                            <div
                              style={{
                                width: '100%',
                                maxWidth: `${qrSize + 20}px`,
                                textAlign: 'center',
                                fontSize: `${Math.max(8, Math.min(12, Math.round(qrSize * 0.095)))}px`,
                                fontWeight: 700,
                                color: el.color || '#1e293b',
                                marginTop: isTop ? 0 : '4px',
                                marginBottom: isTop ? '4px' : 0,
                                whiteSpace: 'pre-line',
                                wordBreak: 'break-word',
                                lineHeight: 1.25,
                              }}
                            >
                              {labelText}
                            </div>
                          )}
                        </div>
                      );
                    })()}

                

                    {/* SERIAL */}
{el.type === 'serial' && (
                      <div
                        style={{ fontSize:`${el.fontSize||12}px`, fontFamily: el.serialStyle==='classic_lcd'?'JetBrains Mono,monospace':el.serialStyle==='handwritten'?'Times New Roman,serif':(el.fontFamily||'Inter'), color:el.serialStyle==='classic_lcd'?'#22c55e':(el.color||'#ea580c'), backgroundColor:el.serialStyle==='classic_lcd'?'#0f172a':'transparent' }}
                        className={`p-1 px-2 border w-full h-full flex items-center gap-1 ${el.serialStyle==='classic_lcd'?'border-slate-800 rounded font-bold':el.serialStyle==='handwritten'?'border-dashed border-slate-300 italic':el.serialStyle==='badge_pill'?'border-amber-500 bg-amber-50/40 rounded-full font-extrabold':'bg-teal-50/50 border-dashed border-teal-600/30 rounded font-bold'}`}
                      >
                        <span className="truncate tracking-widest font-mono">
  {el.serialSettings?.manualMode ? (el.serialSettings.manualValue || formatSerialNumber(el.serialSettings)) : formatSerialNumber(el.serialSettings)}
</span>
                      </div>
                    )}

                    {/* TABLE */}
                    {(() => { var isNarrow58 = pageConfig.width <= 220; return null; })()}
                    {el.type === 'table' && el.columns && el.rows && computedTotals && (
  <div
  ref={(node) => { tableRefs.current[el.id] = node; }}
  style={{
    fontFamily: el.fontFamily || 'Inter',
    width: '100%',
    display: 'block',
    overflow: 'visible',
  }}
  className={`bg-slate-50 border border-slate-200 shadow-inner rounded ${pageConfig.width <= 220 ? 'p-0.5' : 'p-1'}`}
    onMouseDown={(e) => { e.stopPropagation(); if (!lockedElementIds.includes(el.id)) handleMouseDown(e, el, 'drag'); }}
  >
                        {/* Native Table with repeating thead */}
                        <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed', overflow: 'visible' }}>
                          <thead
                            style={{
                              display: 'table-header-group',
                              backgroundColor: el.color || '#1e293b',
                              color: el.color === '#ffffff' ? '#1e293b' : '#ffffff',
                              borderBottom: el.color === '#ffffff' ? '1px solid #cbd5e1' : 'none',
                              letterSpacing: printMode ? '0.02em' : undefined,
                            }}
                            className={`font-bold uppercase ${printMode ? 'text-[10.5px]' : pageConfig.width <= 220 ? 'text-[8px]' : 'text-[10px]'}`}
                          >
                            <tr style={{ backgroundColor: el.color || '#1e293b' }}>
                              {el.columns.map(col => (
                                <th
                                  key={col.id}
                                  style={{
                                    width: `${col.width}%`,
                                    padding: printMode ? '8px 10px' : pageConfig.width <= 220 ? '2px 4px' : '4px 6px',
                                    textAlign: col.align === 'right' ? 'right' : col.align === 'center' ? 'center' : 'left',
                                    verticalAlign: 'middle',
                                  }}
                                >
                                  <div className={`flex flex-col w-full ${col.align === 'right' ? 'items-end' : col.align === 'center' ? 'items-center' : 'items-start'}`}>
                                    <span style={{ whiteSpace: 'nowrap', overflow: 'visible', textOverflow: 'clip', letterSpacing: pageConfig.width <= 302 ? '0' : undefined }} className={`w-full ${col.align === 'right' ? 'text-right' : col.align === 'center' ? 'text-center' : 'text-left'}`}>{col.header}</span>
                                    {bilingualMode && col.arabicHeader && (
                                      <span style={{ direction: 'rtl', fontSize: '7px', opacity: 0.85, fontFamily: '"Noto Sans Arabic",Arial,sans-serif', width: '100%', textAlign: 'right', display: 'block' }}>{col.arabicHeader}</span>
                                    )}
                                  </div>
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody className={`text-slate-800 ${printMode ? 'text-[12px]' : pageConfig.width <= 220 ? 'text-[8px]' : 'text-[10px]'}`}>
                            {el.rows.map((row, rIdx) => (
                              <tr
                                key={row.id}
                                style={{ breakInside: 'avoid', pageBreakInside: 'avoid' }}
                                className={`${printMode ? 'border-b border-slate-100' : ''} ${rIdx % 2 === 1 ? 'bg-blue-50/25' : 'bg-white'}`}
                              >
                                {el.columns?.map(col => {
                                  let display = String(row.values[col.key] ?? '');
                                  if (col.colType === 'serial') {
                                    display = `${col.serialPrefix || ''}${(col.serialStart ?? 1) + rIdx}${col.serialSuffix || ''}`;
                                  } else if (isSerialNoColumn(col)) {
                                    display = String(rIdx + 1);
                                  } else if (col.colType === 'currency' || col.key === 'total') {
                                    const q = parseFloat(String(row.values.qty)) || 0;
                                    const r = parseFloat(String(row.values.rate)) || 0;
                                    display = col.key === 'total' ? `${displaySymbol}${(q * r).toFixed(2)}` : `${displaySymbol}${(parseFloat(display) || 0).toFixed(2)}`;
                                  } else if (col.colType === 'number') {
                                    display = String(parseFloat(display) || 0);
                                  } else if (col.colType === 'date') {
                                    display = display ? new Date(display).toLocaleDateString() : '';
                                  } else if (col.key === 'rate') {
                                    display = `${displaySymbol}${(parseFloat(display) || 0).toFixed(2)}`;
                                  }
                                  const arDisplay = /\d/.test(display) ? arabizeNums(display) : null;
                                  return (
                                    <td
                                      key={col.id}
                                      style={{
                                        width: `${col.width}%`,
                                        wordBreak: 'normal',
                                        overflowWrap: col.key === 'description' ? 'break-word' : 'normal',
                                        whiteSpace: col.key === 'description' ? 'normal' : 'nowrap',
                                        padding: printMode ? '8px 10px' : pageConfig.width <= 220 ? '2px 4px' : '4px 6px',
                                        verticalAlign: 'top',
                                      }}
                                    >
                                      <div className={`flex flex-col w-full ${col.align === 'right' ? 'items-end' : col.align === 'center' ? 'items-center' : 'items-start'}`}>
                                        <span>{display}</span>
                                        {bilingualMode && arDisplay && <div style={{ direction: 'rtl', fontSize: '8px', color: '#64748b', fontFamily: '"Noto Sans Arabic"', width: '100%', textAlign: 'right', marginTop: '2px', display: 'block' }}>{arDisplay}</div>}
                                        {bilingualMode && col.key === 'description' && row.values.arabicDescription && (
                                          <span style={{ direction: 'rtl', fontSize: '8px', color: '#64748b', fontFamily: '"Noto Sans Arabic",Arial,sans-serif', width: '100%', textAlign: 'right', display: 'block', marginTop: '2px' }}>{row.values.arabicDescription as string}</span>
                                        )}
                                      </div>
                                    </td>
                                  );
                                })}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        {/* Totals */}
<div style={{ borderTop: printMode ? '2px solid #cbd5e1' : '2px solid #e2e8f0', breakInside: 'avoid', pageBreakInside: 'avoid', marginLeft: el.totalsLayout === 'full' ? 0 : 'auto' }} className={`bg-slate-50 rounded border border-slate-200 space-y-1 ${printMode ? 'mt-4 p-3.5 text-[11.5px]' : pageConfig.width <= 220 ? 'mt-0 p-1 text-[7px]' : 'mt-1 p-1.5 text-[9px]'} ${el.totalsLayout==='full'?'w-full':pageConfig.width <= 220 ? 'w-[62%] mr-0.5 mb-0.5' : 'w-[45%] mr-1 mb-1'}`}>
  {/* 1. Subtotal (Base) */}
  <div className="flex justify-between">
    <span>{bilingualMode && translateTargetLang !== 'en' ? tl.subtotal : 'Sub'}</span>
    <div className="text-right">
      <div className="font-mono"><span style={symbolStyle}>{displaySymbol}</span>{computedTotals.subtotal.toFixed(2)}</div>
      {bilingualMode && translateTargetLang === 'ar' && <div style={{fontSize:'7px',fontFamily:'"Noto Sans Arabic"',direction:'rtl',color:'inherit'}}>{arabizeNums(computedTotals.subtotal.toFixed(2))} <span style={symbolStyle}>{displaySymbol}</span></div>}
    </div>
  </div>

  {/* 2. DISCOUNTS FIRST */}
  {((el as any).discounts || []).filter((d: any) => d.rate > 0 || d.amount > 0).map((d: any, i: number) => {
    const dAmt = d.amount !== undefined ? Number(d.amount) : (computedTotals.subtotal * d.rate / 100);
    return (
      <div key={i} className="flex justify-between text-red-500">
        <span>{d.label} {d.rate ? `(-${d.rate}%)` : ''}</span>
        <div className="text-right">
          <div className="font-mono">-<span style={symbolStyle}>{displaySymbol}</span>{dAmt.toFixed(2)}</div>
          {bilingualMode && translateTargetLang === 'ar' && <div style={{fontSize:'7px',fontFamily:'"Noto Sans Arabic"',direction:'rtl',color:'#ef4444'}}>-{arabizeNums(dAmt.toFixed(2))} <span style={symbolStyle}>{displaySymbol}</span></div>}
        </div>
      </div>
    );
  })}

  {/* 3. TAXES SECOND */}
  {computedTotals.taxBreakdown && computedTotals.taxBreakdown.length > 0 ? (
    computedTotals.taxBreakdown.map((line, i) => (
      <div key={i} className="flex justify-between text-violet-600">
        <span>{line.name} ({line.rate}%)</span>
        <div className="text-right">
          <div className="font-mono"><span style={symbolStyle}>{displaySymbol}</span>{line.amount.toFixed(2)}</div>
          {bilingualMode && translateTargetLang === 'ar' && <div style={{fontSize:'7px',fontFamily:'"Noto Sans Arabic"',direction:'rtl',color:'inherit'}}>{arabizeNums(line.amount.toFixed(2))} <span style={symbolStyle}>{displaySymbol}</span></div>}
        </div>
      </div>
    ))
  ) : (computedTotals.productTax ?? 0) > 0 && (
    <div className="flex justify-between text-violet-600">
      <span>
        {bilingualMode && translateTargetLang !== 'en' ? tl.tax : 'Total Tax'}
        {' '}({((computedTotals.productTax! / (computedTotals.subtotal || 1)) * 100).toFixed(1)}%)
      </span>
      <div className="text-right">
        <div className="font-mono"><span style={symbolStyle}>{displaySymbol}</span>{computedTotals.productTax!.toFixed(2)}</div>
        {bilingualMode && translateTargetLang === 'ar' && <div style={{fontSize:'7px',fontFamily:'"Noto Sans Arabic"',direction:'rtl',color:'inherit'}}>{arabizeNums(computedTotals.productTax!.toFixed(2))} <span style={symbolStyle}>{displaySymbol}</span></div>}
      </div>
    </div>
  )}
  {((el as any).taxes || []).filter((t: any) => t.rate > 0).map((t: any, i: number) => {
    const taxableBase = computedTotals.subtotal - computedTotals.discount;
    const tAmt = taxableBase * t.rate / 100;
    return (
      <div key={i} className="flex justify-between text-slate-600">
        <span>{t.label} +{t.rate}%</span>
        <div className="text-right">
          <div className="font-mono"><span style={symbolStyle}>{displaySymbol}</span>{tAmt.toFixed(2)}</div>
          {bilingualMode && translateTargetLang === 'ar' && <div style={{fontSize:'7px',fontFamily:'"Noto Sans Arabic"',direction:'rtl',color:'inherit'}}>{arabizeNums(tAmt.toFixed(2))} <span style={symbolStyle}>{displaySymbol}</span></div>}
        </div>
      </div>
    );
  })}
  {el.shippingCost ? <div className="flex justify-between text-slate-600"><span>Shipping:</span><div className="text-right">
  <div className="font-mono"><span style={symbolStyle}>{displaySymbol}</span>{computedTotals.shipping.toFixed(2)}</div>
  {bilingualMode && translateTargetLang === 'ar' && <div style={{fontSize:'7px',fontFamily:'"Noto Sans Arabic"',direction:'rtl',color:'inherit'}}>{arabizeNums(computedTotals.shipping.toFixed(2))} <span style={symbolStyle}>{displaySymbol}</span></div>}
</div></div> : null}
  <div className="flex justify-between font-bold text-blue-700 border-t border-slate-200 pt-0.5">
    <span>TOTAL:{bilingualMode && tl && <div style={{fontSize:'7px',fontFamily:'"Noto Sans Arabic",sans-serif',direction:'rtl'}}>{tl.total}</div>}</span>
    <div className="text-right">
      <div className="font-mono"><span style={symbolStyle}>{displaySymbol}</span>{computedTotals.total.toFixed(2)}</div>
      {bilingualMode && translateTargetLang === 'ar' && <div style={{fontSize:'7px',fontFamily:'"Noto Sans Arabic"',direction:'rtl',color:'#1d4ed8',opacity:0.8}}>{arabizeNums(computedTotals.total.toFixed(2))} <span style={symbolStyle}>{displaySymbol}</span></div>}
    </div>
  </div>
</div>
                      </div>
                    )}

                  </div>
                )}
              </div>
            );
          })}
        </div>
        </div>
      </div>

      {/* Editor help hint */}
{(() => {
  const TIP_DISMISSED_KEY = 'cloudairambo_canvas_tip_dismissed';
  const [show, setShow] = React.useState(
    () => localStorage.getItem(TIP_DISMISSED_KEY) !== '1'
  );

  if (!show) return null;

  return (
    <div
      style={{ fontFamily: 'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif' }}
      className="
        absolute bottom-5 left-1/2 -translate-x-1/2
        z-[120]
        w-[calc(100%-32px)] max-w-[430px]
        bg-white/95 backdrop-blur-md
        text-slate-700
        border border-slate-200
        shadow-xl
        rounded-2xl
        px-4 py-3
        flex items-start gap-3
        select-none pointer-events-auto
      "
    >
      <div className="
        w-9 h-9 shrink-0
        rounded-xl
        bg-blue-50
        flex items-center justify-center
      ">
        <Info className="w-5 h-5 text-blue-600" />
      </div>

      <div className="flex-1 min-w-0">
        <div className="text-[13px] font-bold text-slate-900">
          Quick editing tip
        </div>

        <div className="text-[11px] sm:text-xs text-slate-500 leading-relaxed mt-0.5">
          Select an element, drag to move, resize using the handles,
          and double-click to edit.
        </div>
      </div>

      <button
        type="button"
        aria-label="Close editing tip"
        onClick={() => {
          localStorage.setItem(TIP_DISMISSED_KEY, '1');
          setShow(false);
        }}
        className="
          w-7 h-7 shrink-0
          rounded-full
          flex items-center justify-center
          text-slate-400
          hover:text-slate-700
          hover:bg-slate-100
          transition-colors
          cursor-pointer
        "
      >
        ✕
      </button>
    </div>
  );
})()}

    </main>
  );
}
